import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Typography } from "@/components/ui/typography"
import { useGetAllFeatureFlags } from "@/contexts/FeatureFlagContext"
import { SETTINGS_PATHS } from "@/features/settings/paths"
import { useObjectUrl } from "@/hooks/useObjectUrl"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  useOrgSignatureImage,
  useOrgSignatures,
  type OrganizationSignature,
} from "@/services/api/organizationSignaturesApi"
import { IconSignature } from "@tabler/icons-react"
import { Loader2 } from "lucide-react"
import { useRef, useState } from "react"
import { Link } from "react-router"

export type SelectedSignature = {
  id: string
  /** Stored dimensions when the org set them; null falls back to the loaded
   *  thumbnail's natural size (both columns are nullable). */
  widthPx: number | null
  heightPx: number | null
}

export function PdfSignatureDropdown({
  onSelect,
  disabled,
}: {
  onSelect: (signature: SelectedSignature) => void
  disabled?: boolean
}) {
  const { translate } = useTranslation()
  const flags = useGetAllFeatureFlags()
  // Called unconditionally (rules of hooks) — the flag only gates rendering,
  // same pattern as ConformitySignatureField. Boolean(...): useOrgSignatures'
  // `enabled` param defaults to true, so passing `undefined` through directly
  // (e.g. a flags object that hasn't loaded the key yet) would fetch anyway.
  const { data: signatures, isLoading } = useOrgSignatures(
    Boolean(flags.ENABLE_ORG_SIGNATURES),
  )

  if (!flags.ENABLE_ORG_SIGNATURES) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" disabled={disabled}>
          {disabled ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <IconSignature size={16} />
          )}
          {translate("PDF_VIEWER/ADD_SIGNATURE")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        {isLoading ? (
          <div className="flex items-center gap-1.5 px-2 py-1.5">
            <Loader2 size={14} className="animate-spin text-muted-foreground" />
          </div>
        ) : !signatures || signatures.length === 0 ? (
          <DropdownMenuItem asChild>
            <Link to={SETTINGS_PATHS.signatures}>
              <Typography as="small">
                {translate("PDF_VIEWER/SIGNATURE_EMPTY")}
              </Typography>
            </Link>
          </DropdownMenuItem>
        ) : (
          <>
            {signatures.map((signature) => (
              <SignatureDropdownRow
                key={signature.id}
                signature={signature}
                onSelect={(dims) => onSelect({ id: signature.id, ...dims })}
              />
            ))}
            <DropdownMenuItem
              asChild
              className="cursor-pointer justify-center py-2.5"
            >
              <Link to={SETTINGS_PATHS.signatures}>
                <Typography
                  as="small"
                  className="text-xs font-normal text-muted-foreground"
                >
                  {translate("PDF_VIEWER/SIGNATURE_MANAGE")}
                </Typography>
              </Link>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function SignatureDropdownRow({
  signature,
  onSelect,
}: {
  signature: OrganizationSignature
  onSelect: (dims: { widthPx: number | null; heightPx: number | null }) => void
}) {
  const { data: blob } = useOrgSignatureImage(signature.id)
  const imageUrl = useObjectUrl(blob)
  const imgRef = useRef<HTMLImageElement>(null)
  const [imageLoaded, setImageLoaded] = useState(false)
  // Only the fallback path actually needs the thumbnail's natural size — a
  // signature with stored dimensions can be selected before its image ever
  // loads. Without this gate, a click that lands before onLoad fires reads
  // naturalWidth/Height as 0, which produces a square placement.
  const hasStoredDims = signature.widthPx != null && signature.heightPx != null
  const canSelect = hasStoredDims || imageLoaded

  return (
    <DropdownMenuItem
      disabled={!canSelect}
      className="relative mx-1 my-1 cursor-pointer flex-col items-stretch gap-0 rounded-md p-1.5 hover:bg-accent focus:bg-accent"
      onSelect={() => {
        // Stored width_px/height_px are nullable — fall back to the
        // thumbnail's own natural size so the aspect ratio is still correct.
        const naturalWidth = imgRef.current?.naturalWidth || null
        const naturalHeight = imgRef.current?.naturalHeight || null
        onSelect({
          widthPx: signature.widthPx ?? naturalWidth,
          heightPx: signature.heightPx ?? naturalHeight,
        })
      }}
    >
      <Typography
        as="small"
        className="absolute left-3 top-2 z-10 text-[10px] font-normal leading-none text-muted-foreground"
      >
        {signature.signatoryName}
      </Typography>
      <div className="flex h-16 w-full items-center justify-center rounded bg-white p-2 pt-4">
        {imageUrl ? (
          <img
            ref={imgRef}
            src={imageUrl}
            alt=""
            onLoad={() => setImageLoaded(true)}
            className="max-h-full w-full object-contain"
          />
        ) : (
          <Loader2 size={14} className="animate-spin text-muted-foreground" />
        )}
      </div>
    </DropdownMenuItem>
  )
}
